import { gql } from '@apollo/client';
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { t } from 'i18next';
import { useCallback, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { ErrorAlert } from '~components/ui/Alert';
import { ModalCloseButton } from '~components/ui/ModalCloseButton';
import { ConfigurationStatus } from '~graphql/__generated__/types';
import { fromError } from '~utils/errors';
import { logger } from '~utils/logger';
import { useCreateDisplayConfigurationMutation } from './__generated__/ExportConfigurationModal.graphql';

const ExportFormData = z.object({
  name: z.string().min(1),
  description: z.string().optional(),
  state: z.nativeEnum(ConfigurationStatus),
});
type ExportFormData = z.infer<typeof ExportFormData>;

interface Props {
  isOpen: boolean;
  onCancel: () => void;
  onSuccess?: (id: string) => Promise<void> | void;
  defaultDescription?: string;
  defaultName?: string;
  displayId: string;
}

export function ExportConfigurationModal({
  isOpen,
  onCancel,
  onSuccess,
  defaultDescription,
  defaultName,
  displayId,
}: Props) {
  const toast = useToast();
  const [isDuplicateError, setIsDuplicateError] = useState(false);
  const [notSupportedFirmwareError, setNotSupportedFirmwareError] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isDirty, isValid },
  } = useForm<ExportFormData>({
    defaultValues: {
      name: defaultName,
      description: defaultDescription,
      state: ConfigurationStatus.Unpublished,
    },
    resolver: zodResolver(ExportFormData),
    mode: 'onChange',
  });
  const initialFocusRef = useRef<HTMLInputElement | null>(null);
  const { ref: nameInputRef, ...nameInputProps } = register('name');

  const [exportConfiguration] = useCreateDisplayConfigurationMutation({
    refetchQueries: ['CustomerConfigurationsPage'],
    awaitRefetchQueries: true,
  });

  const handleExport = useCallback(
    async ({ name, description }: ExportFormData) => {
      if (!displayId || !name) {
        logger.error(
          `Both display id and name are required to export config. Received displayId: ${displayId}, name: ${name}`,
        );
        toast({
          status: 'error',
          title: t('exportConfigurationErr'),
          description: t('exportConfigurationErrDesc'),
        });
        return;
      }
      try {
        const { data } = await exportConfiguration({
          variables: {
            input: {
              displayId,
              name,
              description,
            },
          },
        });

        if (!data || !data.createDisplayConfiguration) {
          throw new Error('Export configuration failed.');
        }
        await onSuccess?.(data.createDisplayConfiguration.id);
        setIsDuplicateError(false);

        toast({
          status: 'success',
          title: t('exportConfigurationSuccess'),
          description: (
            <Box whiteSpace="pre-line">
              {t('exportConfigurationSuccessDesc', { oldConfig: defaultName, newConfig: name })}
            </Box>
          ),
        });
        onCancel();
      } catch (err: unknown) {
        if (err instanceof Error && err.message.includes('CONFIGURATION_DUPLICATE_NAME')) {
          setIsDuplicateError(true);
          return;
        } else if (
          err instanceof Error &&
          err.message.includes('FIRMWARE_ANDROID_VERSION_NOT_FOUND')
        ) {
          setNotSupportedFirmwareError(true);
          return;
        }

        toast({
          status: 'error',
          title: t('exportConfigurationErr'),
          description: fromError(err, 'ClonePlaylist'),
        });
      }
    },
    [defaultName, displayId, exportConfiguration, onCancel, onSuccess, toast],
  );

  return (
    <Modal initialFocusRef={initialFocusRef} isOpen={isOpen} onClose={onCancel} size={'3xl'}>
      <form onSubmit={handleSubmit(handleExport)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('ImportConfiguration')}</ModalHeader>
          <ModalCloseButton
            onClick={() => {
              setNotSupportedFirmwareError(false);
              onCancel();
            }}
          />
          <ModalBody>
            <VStack align={'start'} gap={2}>
              <FormControl isRequired isInvalid={Boolean(errors.name)}>
                <FormLabel>{t('configurationName')}</FormLabel>
                <Input
                  ref={(r) => {
                    nameInputRef(r);
                    initialFocusRef.current = r;
                  }}
                  placeholder={t('configurationNamePlaceholder')}
                  {...nameInputProps}
                  onChange={(e) => {
                    nameInputProps.onChange(e);
                    if (isDuplicateError) setIsDuplicateError(false);
                  }}
                />
                <FormErrorMessage>{errors.name?.message}</FormErrorMessage>
              </FormControl>
              <FormControl isInvalid={Boolean(errors.description)}>
                <FormLabel>{t('description')}</FormLabel>
                <Input
                  type="textarea"
                  placeholder={t('configurationDescPlaceholder')}
                  {...register('description')}
                />
                <FormErrorMessage>{errors.description?.message}</FormErrorMessage>
              </FormControl>

              {isDuplicateError && <ErrorAlert>{t('duplicateConfigDesc')}</ErrorAlert>}
              {notSupportedFirmwareError && (
                <ErrorAlert>{t('notSupportedFirmwareError')}</ErrorAlert>
              )}
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button
              variant="ghost"
              colorScheme="blue"
              onClick={() => {
                setNotSupportedFirmwareError(false);
                onCancel();
              }}
              isDisabled={isSubmitting}
            >
              {t('close')}
            </Button>
            <Button
              variant="solid"
              colorScheme="blue"
              marginLeft="3"
              type="submit"
              isDisabled={(!isValid && !isDirty) || isSubmitting || isDuplicateError}
              isLoading={isSubmitting}
            >
              {t('create')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </form>
    </Modal>
  );
}

ExportConfigurationModal.graphql = {
  mutations: {
    createDisplayConfiguration: gql`
      mutation CreateDisplayConfiguration($input: CreateConfigurationInput!) {
        createDisplayConfiguration(input: $input) {
          id
          name
        }
      }
    `,
  },
};
